/**
 * §7.5 Intervention Planner. Runs N candidate NPI baskets against the
 * engine in parallel, then ranks by projected deaths + peak ICU.
 * Closes the loop: ScenarioSimulator explores one basket, this one
 * compares several so a planner can pick before logging the decision.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird, projectHospitalCensus } from '@biomax/plugin-epidemiology';
import { NPI_CATALOG, composeBetaScaler } from '@biomax/plugin-epi-interventions';
import { Sparkline } from '../sparkline';

interface Basket {
  id: string;
  label: string;
  rationale: string;
  npis: string[];
}

const BASKETS: ReadonlyArray<Basket> = [
  {
    id: 'do-nothing',
    label: 'No intervention',
    rationale: 'Counterfactual reference. Establishes worst-case for benchmarking.',
    npis: [],
  },
  {
    id: 'masking-only',
    label: 'Universal masking only',
    rationale: 'Lowest-friction basket. Single intervention, no economic shutdown.',
    npis: ['mask-universal'],
  },
  {
    id: 'mask-distance-light',
    label: 'Masking + distance tier 1',
    rationale: 'Low-cost stack. Voluntary gathering limits + masking.',
    npis: ['mask-universal', 'distance-tier-1'],
  },
  {
    id: 'mask-trace-distance',
    label: 'Masking + tracing + distance tier 2',
    rationale: 'Mid-aggressive. Adds contact tracing + mandatory gathering limits.',
    npis: ['mask-universal', 'contact-tracing-light', 'distance-tier-2'],
  },
  {
    id: 'full-suppression',
    label: 'Full suppression',
    rationale: 'High-cost. Schools + workplaces closed, intensive tracing, distance tier 3.',
    npis: ['mask-universal', 'school-closure', 'workplace-closure', 'contact-tracing-intensive', 'distance-tier-3'],
  },
];

interface BasketResult {
  basket: Basket;
  finalDeaths: number;
  peakI: number;
  peakHosp: number;
  hospSatDay: number | null;
  icuSatDay: number | null;
  trajectory: number[];
}

export function InterventionPlanner() {
  const pathogens = listPathogens();
  const regions = listRegions();

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [regionId, setRegionId] = useState<string>('usa');
  const [duration, setDuration] = useState<number>(180);

  const results: BasketResult[] = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return [];

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
    const N = region.population.total;

    const hospitalBedsPerCapita = (region.healthcareCapacity?.hospitalBedsPer100k ?? 200) / 100_000;
    const icuBedsPerCapita = (region.healthcareCapacity?.icuBedsPer100k ?? 8) / 100_000;
    const hospitalBedsTotal = hospitalBedsPerCapita * N;
    const icuBedsTotal = icuBedsPerCapita * N;

    const ihrFraction = pathogen.icuFraction?.value !== undefined
      ? Math.max(0.02, Math.min(0.20, pathogen.icuFraction.value * 0.5))
      : 0.05;
    const icuFractionOfHosp = pathogen.icuFraction?.value ?? 0.15;
    const losMean = pathogen.hospLengthOfStayDays?.mean ?? 7;
    const losSd = (pathogen.hospLengthOfStayDays?.p95 ?? losMean * 2) / 2;

    return BASKETS.map(basket => {
      const schedule = basket.npis.map(npiId => ({
        npiId, startDay: 14, endDay: duration - 14,
      }));
      const betaScaler = schedule.length > 0 ? composeBetaScaler(schedule, NPI_CATALOG) : undefined;

      const sim = simulateSeird({
        N, I0: 100, beta, gamma, sigma, ifr,
        durationDays: duration, dtDays: 1,
      }, betaScaler ? { betaScaler } : undefined);

      const S = sim.compartments.S!;
      const dailyIncidence: number[] = [];
      for (let i = 1; i < S.length; i++) {
        dailyIncidence.push(Math.max(0, S[i - 1]! - S[i]!));
      }
      const proj = projectHospitalCensus({
        dailyIncidence,
        ihr: ihrFraction,
        losMeanDays: losMean,
        losSdDays: losSd,
        icuFraction: icuFractionOfHosp,
        icuLosMeanDays: losMean + 3,
        icuLosSdDays: losSd,
        hospitalBedsTotal,
        icuBedsTotal,
        surgeMultiplier: 1.0,
      });

      return {
        basket,
        finalDeaths: sim.compartments.D!.at(-1)!,
        peakI: Math.max(...sim.compartments.I!),
        peakHosp: Math.max(...proj.hospitalCensus),
        hospSatDay: proj.hospitalSaturationDay,
        icuSatDay: proj.icuSaturationDay,
        trajectory: sim.compartments.I!,
      };
    });
  }, [pathogenId, regionId, duration]);

  if (results.length === 0) return <div>Loading…</div>;

  // Rank by final deaths ascending.
  const ranked = results.slice().sort((a, b) => a.finalDeaths - b.finalDeaths);
  const baseline = results.find(r => r.basket.id === 'do-nothing')!;
  const region = findRegion(regionId)!;

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.5</span>
        <h1 className="surface__title">Intervention Planner</h1>
      </div>
      <div className="surface__sub">
        Ranks {BASKETS.length} candidate baskets · same engine, same seed, same catalog ·
        deaths averted vs baseline · {region.names.english}
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Duration (days)</span>
            <input type="number" value={duration} min={30} max={730}
              onChange={e => setDuration(parseInt(e.target.value, 10) || 180)} />
          </label>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Basket ranking</span>
          <span className="panel__meta">sorted by projected deaths (ascending)</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Basket</th>
              <th className="num">Deaths</th>
              <th className="num">Averted vs baseline</th>
              <th className="num">Peak I</th>
              <th className="num">Hosp saturation</th>
              <th className="num">ICU saturation</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((r, i) => {
              const averted = baseline.finalDeaths - r.finalDeaths;
              const isBaseline = r.basket.id === 'do-nothing';
              return (
                <tr key={r.basket.id}>
                  <td>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.basket.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--bm-text-muted)', marginTop: 2 }}>{r.basket.rationale}</div>
                  </td>
                  <td className="num">{Math.round(r.finalDeaths).toLocaleString()}</td>
                  <td className="num" style={{ color: isBaseline ? 'var(--bm-text-muted)' : 'var(--bm-accent)' }}>
                    {isBaseline ? '—' : Math.round(averted).toLocaleString()}
                  </td>
                  <td className="num">{(r.peakI / 1e6).toFixed(2)}M</td>
                  <td className="num">{r.hospSatDay !== null ? `day ${r.hospSatDay}` : '—'}</td>
                  <td className="num">{r.icuSatDay !== null ? `day ${r.icuSatDay}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Infectious trajectories · all baskets</span>
          <span className="panel__meta">stacked for visual ranking</span>
        </div>
        {ranked.map(r => (
          <Sparkline
            key={r.basket.id}
            values={r.trajectory}
            label={r.basket.label}
            stroke={r.basket.id === 'do-nothing' ? 'var(--bm-danger)'
              : r.basket.id === 'full-suppression' ? 'var(--bm-accent)'
              : 'var(--bm-signal)'}
          />
        ))}
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">How to use this</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          The planner is <strong>not</strong> prescriptive. It surfaces tradeoffs: maximum suppression
          minimises deaths but imposes the highest economic + social cost (school + workplace closure).
          Universal masking alone may avert {Math.round(baseline.finalDeaths - (results.find(r => r.basket.id === 'masking-only')?.finalDeaths ?? 0)).toLocaleString()}
          {' '}deaths at minimal cost. Choose a basket, then log the decision + rationale to §7.17
          Decision Log. <strong>Human-in-the-loop is invariant:</strong> PaCC never enacts policy.
        </div>
      </div>
    </div>
  );
}
